import seedFlexLogo from '@assets/icons/seedflexLogo.svg';
import seedFlexLogoBoost from '@assets/icons/BoostSF-Inline-Logo.svg';
import seedflexTabLogo from '@assets/icons/seedflexTabLogo.svg';
import seedflexTabLogoBoost from '@assets/icons/boostseedflexTabLogo.svg';
import {
  LANGUAGES,
  PATH,
  WHATS_APP_NUMBER,
  WHATS_APP_NUMBER_ID,
} from '@common/constants';
import {Avatar, useMediaQuery} from '@mui/material';
import HomeIcon from '@shared/icons/HomeIcon';
import ProfileIcon from '@shared/icons/Profile';
import QuestionIcon from '@shared/icons/QuestionIcon';
import {useEffect, useState} from 'react';
import {useLocation} from 'react-router';
import {useTranslation} from 'react-i18next';
import Dropdown from '@shared/components/Dropdown';
import {ILanguage} from '@common/interface';
import PrimaryLogout from '@assets/icons/PrimaryLogout.svg';
import BoostLogout from '@assets/icons/BoostLogout.svg';
import IndriveLogout from '@assets/icons/IndriveLogout.svg';
import JaideeLogout from '@assets/icons/JaideeLogout.svg';
import JaideeLogo from '@assets/icons/JaideeLogo.svg';
import JaideeSmall from '@assets/icons/JaideeSmall.svg';
import GreenTranslate from '@assets/icons/TranslationJaidee.svg';

import {RootState, dispatch} from '@store';
import {
  loginActions,
  loginReducerLogout,
} from '@pages/Login/auth/store/loginReducer';
import {logoutApi} from '@pages/Login/api';
import LogoutModal from '@pages/Login/component/LogoutModal';
import {topupLoanApplicationActions} from '@pages/LoanApplication/store/topupLoanApplicationReducer';
import {loanApplicationActions} from '@pages/LoanApplication/store/loanApplicationReducer';
import {useSelector} from 'react-redux';
import {useTheme} from 'styled-components';
import {getCountryCodeFromUrl, getImageApi} from '@shared/api/api';
import {
  AvatarWrapper,
  GridItem,
  IconWrapper,
  ListItemTextContent,
  Logo,
  LogoutButton,
  SmartFormLogoRow,
  SidebarDropdown,
  SidebarItem,
  SidebarItems,
  SidebarWrapper,
} from './Sidebar.style';
import Stepper from '../Stepper';

interface ISidebar {
  icon: React.ReactNode;
  link: string;
  text: string;
  id: string;
}

export const items = (
  t: (key: string) => string,
  activeItem: string,
  activeIcon: string,
  countryCode: string
): ISidebar[] => [
  {
    icon: (
      <HomeIcon
        color={activeItem === 'Home' ? activeIcon : '#757575'}
        width='24px'
        height='24px'
      />
    ),
    link: PATH.DASHBOARD,
    text: t('sidebar.MP_SIDEBAR_HOME'),
    id: 'Home',
  },
  {
    icon: (
      <ProfileIcon
        color={activeItem === 'My Profile' ? activeIcon : '#757575'}
        width='24px'
        height='24px'
      />
    ),
    text: t('sidebar.MP_SIDEBAR_PROFILE'),
    link: PATH.PROFILE,
    id: 'My Profile',
  },
  {
    icon: (
      <QuestionIcon
        color={activeItem === 'Contact Us' ? activeIcon : '#757575'}
        width='24px'
        height='24px'
      />
    ),
    text: t('sidebar.MP_SIDEBAR_CONTACT'),
    link:
      countryCode === 'TH'
        ? 'mailto:contact@jaidee.co.th'
        : `https://wa.me/${
            countryCode === 'ID' ? WHATS_APP_NUMBER_ID : WHATS_APP_NUMBER
          }`,
    id: 'Contact Us',
  },
];

const isDirectDebitPath = (pathname: string) =>
  ['/direct-debit-setup', '/paylater-direct-debit-setup'].includes(pathname);

const isPublicPath = (pathname: string) =>
  [
    '/direct-debit-setup',
    '/paylater-direct-debit-setup',
    '/payment',
    '/payment/status',
  ].includes(pathname);

const isExternalFormPath = (pathname: string) =>
  /^\/form\/e\/[^/]+\/?$/.test(pathname);

const shouldRenderLogo = (
  directDebit: boolean,
  ddStep: number,
  isIndrive: boolean,
  isMobScreen: boolean
) => (!directDebit || ddStep !== 0) && !isIndrive && !isMobScreen;

const FORM_LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  id: 'Bahasa - Bahasa Indonesia',
  th: 'ภาษา - ภาษาไทย',
  ms: 'Bahasa - Bahasa Melayu',
};

interface SidebarBrandLogoProps {
  isExternalFormPage: boolean;
  smartFormPartnerLogo: string | null;
  isTabScreen: boolean;
  isBoostTheme: boolean;
  isJaideeTheme: boolean;
  sidebarLogo: string;
  tabSidebarLogo: string;
}

function SmartFormPartnerLogo({name}: {name: string}) {
  const [image, setImage] = useState<{name: string; src: string} | null>(null);

  useEffect(() => {
    let active = true;
    getImageApi({name: `${name}.svg`})
      .then((response) => {
        if (active && typeof response.data === 'string') {
          setImage({name, src: response.data});
        }
      })
      .catch(() => {
        if (active) setImage(null);
      });
    return () => {
      active = false;
    };
  }, [name]);

  if (image?.name !== name) return null;
  return <img src={image.src} alt={`${name} logo`} />;
}

export function SidebarBrandLogo({
  isExternalFormPage,
  smartFormPartnerLogo,
  isTabScreen,
  isBoostTheme,
  isJaideeTheme,
  sidebarLogo,
  tabSidebarLogo,
}: SidebarBrandLogoProps) {
  const currentLogo = isTabScreen ? tabSidebarLogo : sidebarLogo;
  const partnerName = smartFormPartnerLogo
    ?.toLowerCase()
    .replace(/[^a-z]/g, '');

  if (isExternalFormPage && partnerName === 'boost') {
    return (
      <Logo
        $isBoost={!isTabScreen}
        $isJaidee={false}
        src={isTabScreen ? seedflexTabLogoBoost : seedFlexLogoBoost}
        alt='Boost Seedflex logo'
      />
    );
  }

  if (isExternalFormPage && partnerName && partnerName !== 'seedflex') {
    return (
      <SmartFormLogoRow $isTabScreen={isTabScreen}>
        <Logo
          $isBoost={!isTabScreen && isBoostTheme}
          $isJaidee={!isTabScreen && isJaideeTheme}
          src={currentLogo}
          alt={isTabScreen ? 'TabLogo' : 'Logo'}
        />
        <SmartFormPartnerLogo name={partnerName} />
      </SmartFormLogoRow>
    );
  }

  return (
    <Logo
      $isBoost={!isTabScreen && isBoostTheme}
      $isJaidee={!isTabScreen && isJaideeTheme}
      src={currentLogo}
      alt={isTabScreen ? 'TabLogo' : 'Logo'}
    />
  );
}

interface SidebarLanguageToggleProps {
  isExternalFormPage: boolean;
  smartFormLanguages: string[];
  country: string;
  countryCode: string;
  isMobScreen: boolean;
  isIndrive: boolean;
  isTabScreen: boolean;
  selectedLang: ILanguage | undefined;
  onLanguageChange: (lng: string) => void;
  onSelectLang: (lang?: ILanguage) => void;
  onToggleTabLang: () => void;
}

function SidebarLanguageToggle({
  isExternalFormPage,
  smartFormLanguages,
  country,
  countryCode,
  isMobScreen,
  isIndrive,
  isTabScreen,
  selectedLang,
  onLanguageChange,
  onSelectLang,
  onToggleTabLang,
}: SidebarLanguageToggleProps) {
  const {i18n} = useTranslation();

  if (isExternalFormPage && smartFormLanguages.length < 2) return null;
  if (
    !isExternalFormPage &&
    (country === 'MY' || countryCode === 'MY' || isMobScreen || isIndrive)
  ) {
    return null;
  }

  if (isTabScreen && !isExternalFormPage) {
    return (
      <SidebarDropdown>
        <AvatarWrapper
          onClick={onToggleTabLang}
          style={{
            justifyContent: 'center',
            display: 'flex',
          }}>
          <Avatar alt='Translate' src={GreenTranslate} />
        </AvatarWrapper>
      </SidebarDropdown>
    );
  }

  const languageOptions = isExternalFormPage
    ? smartFormLanguages.map((language, index) => ({
        key: index,
        value: language,
        text: FORM_LANGUAGE_LABELS[language] || language,
      }))
    : LANGUAGES;

  const currentFormLanguage = smartFormLanguages.find((language) =>
    i18n.language.toLowerCase().startsWith(language)
  );

  const selectedValue = isExternalFormPage
    ? currentFormLanguage || smartFormLanguages[0]
    : selectedLang?.value || 'en';

  return (
    <SidebarDropdown>
      <Dropdown
        value={selectedValue}
        manuList={languageOptions.map((lang) => ({
          value: lang.value,
          text: lang.text,
        }))}
        handleChange={(val) => {
          const selected = languageOptions.find((lang) => lang.value === val);
          onSelectLang(selected);
          onLanguageChange(val);
        }}
        placeholderText=''
      />
    </SidebarDropdown>
  );
}

export default function Sidebar() {
  const {
    stepToRedirect,
    step,
    countryCode,
    theme,
    smartFormLanguages,
    smartFormPartnerLogo,
  } = useSelector((state: RootState) => state.login);
  const ddStep = useSelector((state: RootState) => state.directDebit.step);
  const location = useLocation();
  const isTabScreen = useMediaQuery('(max-width:1120px)');
  const isMobScreen = useMediaQuery('(max-width:480px)');
  const isIndrive = theme === 'indrive';

  const [activeItem, setActiveItem] = useState<string>('Home');
  const [open, setOpen] = useState<boolean>(false);
  const {t, i18n} = useTranslation();
  const [selectedLang, setSelectedLang] = useState<ILanguage>();
  const color = useTheme() as {baseColors?: {activeMenuText?: string}};
  const activeIcon = color.baseColors?.activeMenuText || '';

  const handleItemClick = (item: string) => {
    setActiveItem(item);
  };
  const country = getCountryCodeFromUrl();

  const LogoutResponse = async () => {
    const res = await logoutApi();
    if (res?.data?.message === 'Log out successfully') {
      dispatch(loginReducerLogout());
      dispatch(loginActions.setIsShowOtp(false));
      dispatch(loginActions.setIsSelectBRN(false));
      dispatch(loginActions.setIsLogout(true));
      dispatch(loginActions.setCountryCode(''));
      dispatch(topupLoanApplicationActions.resetState());
      dispatch(loanApplicationActions.resetState());
    }
  };
  const handleLogout = () => {
    LogoutResponse();
  };
  const handleClickLogoutIcon = () => {
    if (isIndrive) {
      window.parent.postMessage(
        {
          type: 'EXIT_APPLICATION',
          payload: {},
        },
        '*'
      );
    } else {
      setOpen(true);
    }
  };
  const handleCloseModal = () => {
    setOpen(false);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const handleChangeLang = () => {
    const currentLang = i18n.language;
    const nextLang = LANGUAGES.find(
      (lang) => !currentLang.startsWith(lang.value)
    );
    const fallbackLang = currentLang.startsWith('en')
      ? LANGUAGES[1].value
      : 'en';
    i18n.changeLanguage(nextLang?.value || fallbackLang);
  };

  const directDebit = isDirectDebitPath(location.pathname);
  const isExternalFormPage = isExternalFormPath(location.pathname);
  const publicUrl = isPublicPath(location.pathname);

  useEffect(() => {
    const foundLang = LANGUAGES.find(
      (lang) => lang.value === localStorage.getItem('i18nextLng')
    );
    if (foundLang) setSelectedLang(foundLang);
  }, []);

  const isBoostTheme = theme === 'boost';
  const isJaideeTheme = theme === 'jaidee';

  const sidebarLogo =
    {
      boost: seedFlexLogoBoost,
      jaidee: JaideeLogo,
      default: seedFlexLogo,
      indrive: seedFlexLogo,
    }[theme] || seedFlexLogo;

  const logoutBtn = {
    default: PrimaryLogout,
    boost: BoostLogout,
    indrive: IndriveLogout,
    jaidee: JaideeLogout,
  }[theme];

  const tabSidebarLogo = {
    default: seedflexTabLogo,
    boost: seedflexTabLogoBoost,
    indrive: seedflexTabLogo,
    jaidee: JaideeSmall,
  }[theme];

  const renderItems = () => {
    if (publicUrl || isIndrive || isExternalFormPage) return '';
    if (stepToRedirect) {
      return (
        <div className='stepper-section'>
          <Stepper currentStep={step} orientation='vertical' />
        </div>
      );
    }
    return items(t, activeItem, activeIcon, countryCode).map((item) => (
      <SidebarItem
        $isMobScreen={isMobScreen}
        key={item.id}
        to={item.link}
        target={item.id.includes('Contact') ? '_blank' : '_self'}
        onClick={() => handleItemClick(item.id)}
        $isActive={activeItem === item.id}>
        <IconWrapper
          $isMobScreen={isMobScreen}
          $isActive={activeItem === item.id}
          $isBoost={isBoostTheme}>
          {item.icon}
        </IconWrapper>
        <ListItemTextContent
          $isMobScreen={isMobScreen}
          $isActive={activeItem === item.id}
          primary={item.text}
        />
      </SidebarItem>
    ));
  };

  if (isIndrive && isMobScreen && !isExternalFormPage) return null;

  return (
    <GridItem item xs={9.65} sm={4} md={2.45} lg={2.45} xl={1.8}>
      {location.pathname !== '/' && isMobScreen && !isExternalFormPage ? (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ) : (
        <SidebarWrapper
          $isMobScreen={isMobScreen}
          $isSmartForm={isExternalFormPage}>
          <SidebarItems
            $isMobScreen={isMobScreen}
            $isSmartForm={isExternalFormPage}>
            {shouldRenderLogo(directDebit, ddStep, isIndrive, isMobScreen) && (
              <SidebarBrandLogo
                isExternalFormPage={isExternalFormPage}
                smartFormPartnerLogo={smartFormPartnerLogo}
                isTabScreen={isTabScreen}
                isBoostTheme={isBoostTheme}
                isJaideeTheme={isJaideeTheme}
                sidebarLogo={sidebarLogo}
                tabSidebarLogo={tabSidebarLogo ?? seedflexTabLogo}
              />
            )}
            {renderItems()}
          </SidebarItems>
          <SidebarLanguageToggle
            isExternalFormPage={isExternalFormPage}
            smartFormLanguages={smartFormLanguages}
            country={country}
            countryCode={countryCode}
            isMobScreen={isMobScreen}
            isIndrive={isIndrive}
            isTabScreen={isTabScreen}
            selectedLang={selectedLang}
            onLanguageChange={changeLanguage}
            onSelectLang={setSelectedLang}
            onToggleTabLang={handleChangeLang}
          />
          {!publicUrl && !isIndrive && !isExternalFormPage && (
            <LogoutButton onClick={handleClickLogoutIcon}>
              <AvatarWrapper>
                <Avatar alt='Logout' src={logoutBtn ?? PrimaryLogout} />
              </AvatarWrapper>
              <div className='logout-text'>
                {t('sidebar.MP_SIDEBAR_LOGOUT')}
              </div>
            </LogoutButton>
          )}

          <LogoutModal
            isOpen={open}
            handleLogout={handleLogout}
            handleCancle={handleCloseModal}
          />
        </SidebarWrapper>
      )}
    </GridItem>
  );
}
